import React, { forwardRef, useMemo } from 'react';
import { CircularProgress, FormControl, FormHelperText, InputLabel } from '@mui/material';
import ReactSelect, { MultiValue, SingleValue } from 'react-select';
import { POLICY_ACK_STATUSES } from 'views/App/Portal/Reporting/PolicyAcknowledgments/_types/policyAcknowledgements';
import { translate as t } from 'hooks/useTranslations';

interface StatusOption {
  value: string;
  label: string;
}

export type StatusPickerProps = {
  allowBlank?: boolean;
  allowMultiple?: boolean;
  id: string;
  label?: string;
  value?: string[];
  onChange: (value: string[]) => void;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  error?: any;
  helperText?: string | undefined;
  size?: string;
};

const StatusPicker = forwardRef(
  (
    {
      allowBlank = false,
      allowMultiple = false,
      id,
      label,
      value = [],
      onChange,
      placeholder,
      disabled = false,
      required,
      error,
      helperText,
      size,
    }: StatusPickerProps,
    ref
  ) => {
    const statusOptions = [
      ...(allowBlank ? [{ label: t(`reporting.policyAcknowledgments.status.ALL`), value: '' }] : []),
      ...POLICY_ACK_STATUSES.map((x) => ({ label: t(`reporting.policyAcknowledgments.status.${x}`), value: x })),
    ];

    const selectedOptions = useMemo(() => {
      return statusOptions.filter((x) => (value ?? []).includes(x.value));
    }, [value, statusOptions]);

    return (
      <FormControl
        key={id}
        fullWidth
        error={!!error}
        sx={{
          p: 0,
          paddingTop: !!label ? '1rem' : 0,
        }}
      >
        {!!label && (
          <InputLabel
            required={required}
            sx={{
              fontSize: '0.95rem',
              shrink: {
                transform: 'translate(0px, -8px) scale(1) !important',
                backgroundColor: 'transparent',
              },
            }}
          >
            {label}
          </InputLabel>
        )}
        <ReactSelect
          name={id}
          value={selectedOptions}
          options={statusOptions}
          placeholder={placeholder}
          menuPortalTarget={document.body}
          styles={{
            menuPortal: (base) => ({ ...base, zIndex: 9999 }),
            menu: (base) => ({ ...base, width: 300 }),
            input: (base) => ({
              ...base,
              boxShadow: 'none',
              outline: 0,
              minHeight: size === 'small' ? 26 : 45,
            }),
          }}
          menuPosition='fixed'
          menuPlacement='auto'
          isMulti={allowMultiple}
          isSearchable
          isDisabled={disabled}
          onChange={(newValue) => {
            if (Array.isArray(newValue)) {
              const newVals = newValue as MultiValue<StatusOption>;
              onChange((newValue ?? []).map((x) => x.value));
            } else {
              const newVal = newValue as SingleValue<StatusOption>;
              onChange(!!newVal ? [newVal.value] : []);
            }
          }}
        />
        {!!error && <FormHelperText error>{error.message}</FormHelperText>}
        {!!helperText && <FormHelperText>{helperText}</FormHelperText>}
      </FormControl>
    );
  }
);

export default StatusPicker;
